"use client";

import {
  ChevronUp,
  CreditCard,
  Languages,
  Moon,
  MoonStar,
  Sun,
  User,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import type { UserWithCompanies } from "@/components/auth/auth";
import React, { FC, useState } from "react";
import { useAppStore, selectUser } from "@/store/appStore";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import clsx from "clsx";
import Link from "next/link";
import { signOut } from "next-auth/react";
import { useTheme } from "next-themes";
import FlagComponent from "@/components/ui/flag/flag";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const THEMES = {
  light: {
    value: "Light",
    inverse: "Dark",
  },
  dark: {
    value: "Dark",
    // Removed duplicate/incorrect imports
  },
  "dark-green": {
    value: "Dark Green",
    inverse: "Light",
  },
  system: {
    value: "System",
    inverse: "System",
  },
};

const UserMenu: FC = () => {
  const user: UserWithCompanies | null = useAppStore(selectUser);
  const [open, setOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const currentTheme = theme as keyof typeof THEMES;

  const setLanguage = (language: string) => {
    console.log("setLanguage", language);
  };

  return (
    <div className="p-md ">
      <DropdownMenu modal={false} onOpenChange={setOpen}>
        <Button
          asChild
          variant={"ghost"}
          className={"w-full justify-start !p-sm !py-[25px] flex"}
        >
          <DropdownMenuTrigger className="flex flex-row">
            <div className="flex flex-row align-middle items-center space-x-sm">
              <Avatar className="project-icon bg-foreground/5 rounded-sm">
                <AvatarFallback>{user?.givenName?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="project-name text-foreground ">
                {user?.givenName
                  ? user.givenName + " " + user.familyName
                  : "No User"}
              </div>
            </div>
            <div className="text-foreground ml-auto ">
              <ChevronUp
                className={clsx(
                  "size-4 transition-all shrink-0 opacity-50 ",
                  open && "rotate-180"
                )}
              />
            </div>
          </DropdownMenuTrigger>
        </Button>
        <DropdownMenuContent
          align="start"
          className={"mr-2 w-[--radix-dropdown-menu-trigger-width]"}
        >
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <Link href="/profile">
            <DropdownMenuItem>
              <User />
              Profile
            </DropdownMenuItem>
          </Link>
          <DropdownMenuSeparator className="bg-foreground/5" />

          {/* Theme Submenu */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="p-0 pr-sm">
              <div className="project-name text-foreground ">
                {user?.givenName
                  ? user.givenName + " " + user.familyName
                  : "No User"}
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => setTheme("light")}>
                  <Sun />
                  Light
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("dark")}>
                  <Moon /> Dark
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("dark-green")}>
                  <Moon /> Dark Green
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("system")}>
                  System
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          {/* End of Theme Submenu */}

          {/* Language Submenu */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="p-0 pr-sm">
              <DropdownMenuItem>
                <Languages />
                Language
              </DropdownMenuItem>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => setLanguage("EN_US")}>
                  <FlagComponent size="S" currencyCode={"USD"} />
                  English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage("HU_HU")}>
                  <FlagComponent size="S" currencyCode={"HUF"} />
                  Magyar
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuSeparator className="bg-foreground/5" />
          <DropdownMenuItem onClick={() => signOut()}>Logout</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export { UserMenu };
